import { z } from "zod"

import {
  platformProviderCodeV1Schema,
  platformProviderEnvironmentV1Schema,
  platformProviderFailureCodeV1Schema,
  platformProviderHealthV1Schema,
  platformProviderIntegrationProjectionV1Schema,
} from "./platform-provider-integration-v1.js"
import { platformCommandEnvelopeV1Schema } from "./platform-command-v1.js"
import { platformQueryEnvelopeV1Schema } from "./tenant-directory-v1.js"
import { tenantIdSchema } from "./identifiers.js"

export const platformProviderRetryActionV1Schema = z.enum([
  "verify_setup",
  "verify_webhook",
  "refresh_quota",
  "reconcile",
])

export const platformProviderReadinessFiltersV1Schema = z.object({
  environment: platformProviderEnvironmentV1Schema.nullable(),
  providerCode: platformProviderCodeV1Schema.nullable(),
  credentialScope: z.enum(["platform", "tenant"]).nullable(),
  exactTenantId: tenantIdSchema.nullable(),
  actionRequiredOnly: z.boolean(),
}).strict().superRefine((filters, context) => {
  if (
    filters.credentialScope === "platform"
    && filters.exactTenantId !== null
  ) {
    context.addIssue({
      code: "custom",
      path: ["exactTenantId"],
      message: "Platform credential filters cannot carry a Tenant target.",
    })
  }
})

export const platformProviderReadinessQueryV1Schema = z.object({
  envelope: platformQueryEnvelopeV1Schema.extend({
    queryType: z.literal("PlatformProviderReadiness"),
  }),
  filters: platformProviderReadinessFiltersV1Schema,
  asOf: z.iso.datetime({ offset: true }),
  page: z.object({
    limit: z.number().int().min(1).max(100),
    cursor: z.uuid().nullable(),
  }).strict(),
}).strict()

export const platformProviderDiagnosticCheckV1Schema = z.object({
  check: z.enum([
    "configuration",
    "credential_rotation",
    "webhook",
    "quota",
    "reconciliation",
  ]),
  state: platformProviderHealthV1Schema,
  failureCode: platformProviderFailureCodeV1Schema.nullable(),
  retryAction: platformProviderRetryActionV1Schema.nullable(),
}).strict()

export const platformProviderReadinessItemV1Schema =
  platformProviderIntegrationProjectionV1Schema.extend({
    checks: z.array(platformProviderDiagnosticCheckV1Schema).length(5),
    availableActions: z.array(platformProviderRetryActionV1Schema).max(4),
  }).strict()

export const platformProviderReadinessPageV1Schema = z.object({
  responseVersion: z.literal(1),
  generatedAt: z.iso.datetime({ offset: true }),
  partial: z.boolean(),
  items: z.array(platformProviderReadinessItemV1Schema).max(100),
  nextCursor: z.uuid().nullable(),
  redacted: z.literal(true),
}).strict()

export const retryPlatformProviderIntegrationV1Schema = z.object({
  envelope: platformCommandEnvelopeV1Schema.safeExtend({
    commandType: z.literal("RetryPlatformProviderIntegration"),
  }),
  action: platformProviderRetryActionV1Schema,
  integrationId: z.uuid(),
  expectedIntegrationVersion: z.number().int().positive(),
}).strict().superRefine((command, context) => {
  if (
    command.envelope.idempotencyNamespace !== "provider.reconciliation.retry"
    || command.envelope.target.targetType !== "provider_integration"
    || String(command.envelope.target.targetId) !== command.integrationId
    || command.envelope.target.expectedVersion
      !== command.expectedIntegrationVersion
  ) {
    context.addIssue({
      code: "custom",
      path: ["envelope", "target"],
      message: "Provider retry must target the exact Integration version.",
    })
  }
})

export const platformProviderRetryResultV1Schema = z.object({
  responseVersion: z.literal(1),
  platformCommandId: z.uuid(),
  integrationId: z.uuid(),
  action: platformProviderRetryActionV1Schema,
  outcome: z.enum(["succeeded", "failed", "uncertain"]),
  stableFailureCode: platformProviderFailureCodeV1Schema.nullable(),
  replayed: z.boolean(),
  observedAt: z.iso.datetime({ offset: true }),
  credentialDomainPreserved: z.literal(true),
  eventNamespacePreserved: z.literal(true),
  redacted: z.literal(true),
}).strict().superRefine((result, context) => {
  if (
    result.outcome === "succeeded"
      ? result.stableFailureCode !== null
      : result.stableFailureCode === null
  ) {
    context.addIssue({
      code: "custom",
      path: ["stableFailureCode"],
      message: "Provider retry outcome and stable failure code must agree.",
    })
  }
})

export type PlatformProviderRetryActionV1 = z.infer<
  typeof platformProviderRetryActionV1Schema
>
export type PlatformProviderReadinessFiltersV1 = z.infer<
  typeof platformProviderReadinessFiltersV1Schema
>
export type PlatformProviderReadinessItemV1 = z.infer<
  typeof platformProviderReadinessItemV1Schema
>
export type PlatformProviderReadinessPageV1 = z.infer<
  typeof platformProviderReadinessPageV1Schema
>
export type RetryPlatformProviderIntegrationV1 = z.infer<
  typeof retryPlatformProviderIntegrationV1Schema
>
export type PlatformProviderRetryResultV1 = z.infer<
  typeof platformProviderRetryResultV1Schema
>
